/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { NavigatorService } from '@services/navigator/navigator.service';
import { AppService } from '@services/app/app.service';
import { UiService } from '@services/ui/ui.service';
import { LabelsService } from '@services/labels/labels.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiErrorDTO, MessageDTO } from '@api/models';
import { ERRORS, NotificationSkipModel } from '@core/models';
import { ErrorService } from '@core/services/error/error.service';
import { RouteCodes } from '@core/config/route-codes';
import { LoggerService } from '@core/services/logger/logger.service';
import { AuthRequestService } from '@core/services/auth/auth-request.service';
import { ApiErrorDecoder } from '@core/utils';

export interface ReqConfigModel {
  errSkip: NotificationSkipModel;
  errMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptorService implements HttpInterceptor {
  constructor(
    private appService: AppService,
    private uiService: UiService,
    private navigator: NavigatorService,
    private labelsService: LabelsService,
    private errorService: ErrorService,
    private loggerService: LoggerService,
    private authRequestService: AuthRequestService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const errorConfig = this.getReqConfig();

    this.logRequest(req);
    this.errorService.doNextNotification();

    return next.handle(req).pipe(
      map((event: HttpResponse<any>) => this.handleSuccess(event)),
      catchError((event: HttpErrorResponse) =>
        this.handleError(event, errorConfig)
      )
    );
  }

  /**
   * crea la configurazione per la gestione dell'errore della richiesta corrente
   */
  private getReqConfig(): ReqConfigModel {
    return {
      errSkip: this.errorService.notificationSkip,
      errMessage: 'err.something_went_wrong'
    };
  }

  /**
   * gestisce il success
   */
  private handleSuccess(event: HttpResponse<any>): HttpResponse<any> {
    if (
      event.body &&
      event.body.metadata &&
      event.body.metadata.messages instanceof Array &&
      event.body.metadata.messages.length > 0
    ) {
      this.showMessageToast(event.body.metadata.messages[0] as MessageDTO);
    }

    return event;
  }

  /**
   * gestisce l'errore
   */
  private handleError(
    event: HttpErrorResponse,
    config: ReqConfigModel
  ): Observable<never> {
    this.logError(event);

    this.redirectOnError(event, config).then(res => {
      if (!res.done && this.isErrorToShow(event, config)) {
        this.showError(event, config);
      }
    });

    return throwError(event);
  }

  /**
   * stabilisce se l'errore è da visualizzare
   */
  private isErrorToShow(
    event: HttpErrorResponse,
    config: ReqConfigModel
  ): boolean {
    const decoder = this.getErrorDecoder(event, config);

    return (
      !this.isLogUrl(event.url) &&
      (!config.errSkip ||
        config.errSkip.when === 'never' ||
        (config.errSkip.when === 'code' &&
          config.errSkip.code !== decoder.getCode()))
    );
  }

  /**
   * applica redirect sull'errore in base alla configurazione
   */
  private redirectOnError(
    event: HttpErrorResponse,
    config: ReqConfigModel
  ): Promise<{ done: boolean }> {
    const decoder = this.getErrorDecoder(event, config);

    if (event.status === 401) {
      return this.redirectUnauthorized();
    } else {
      return this.redirectError(decoder.getCode());
    }
  }

  /**
   * effettua il redirect per utenza non autorizzata se lo prevede la configurazione;
   * restituisce true se il redirect viene effettuato
   */
  private redirectUnauthorized(): Promise<{ done: boolean }> {
    if (!this.isUnauthorizedToRedirect()) {
      return Promise.resolve({ done: false });
    }

    this.forceAppLoaderClosing();

    return this.navigator
      .navigateToError(ERRORS.sessionExpired)
      .then(res => ({ done: res }));
  }

  /**
   * effettua il redirect sull'errore se è previsto dalla configurazione
   * restituisce true se il redirect viene effettuato
   */
  private redirectError(errorCode: string): Promise<{ done: boolean }> {
    const redirectPath = this.getRedirectPath(errorCode);

    if (!redirectPath) {
      return Promise.resolve({ done: false });
    }

    this.forceAppLoaderClosing();

    return this.navigator
      .navigateToMainRoute(redirectPath)
      .then(res => ({ done: res }));
  }

  /**
   * restituisce il path di redirect impostato per il codice di errore
   */
  private getRedirectPath(errorCode: string): RouteCodes {
    const config = this.appService.getConfig().generic;

    return config.errorsWithRedirect && config.errorsWithRedirect[errorCode];
  }

  /**
   * stabilisce se è necessario fare redirect su errore di utenza non autorizzata
   */
  private isUnauthorizedToRedirect(): boolean {
    return this.appService.getConfig().generic.redirectUnauthorized === true;
  }

  /**
   * in caso di redirect è necessario chiudere l'eventuale app loader aperto
   * che altrimenti non chiuderebbero
   */
  private forceAppLoaderClosing(): Promise<void> {
    return this.uiService.hideAppLoader(true);
  }

  /**
   * visualizza l'errore
   */
  private showError(event: HttpErrorResponse, config: ReqConfigModel) {
    this.errorService.showErrorFromApi(this.getErrorDecoder(event, config));
  }

  /**
   * visualizza messaggio di warning
   */
  private async showMessageToast(message: MessageDTO): Promise<void> {
    if (!message || !message.code) {
      return;
    }

    const label = this.labelsService.getLabel(`wrn.message_${message.code}`);

    switch (message.type) {
      case 'ERROR':
        return this.uiService.showErrorToast(label);
      case 'SUCCESS':
        return this.uiService.showSuccessToast(label);
      case 'INFO':
        return this.uiService.showInfoToast(label);
      case 'WARNING':
      default:
        return this.uiService.showWarningToast(label);
    }
  }

  /**
   * logga la richiesta solo se non è l'api di log
   */
  private logRequest(req: HttpRequest<any>): void {
    if (this.isUrlToLog(req.url)) {
      this.loggerService.logHttpReq(req);
    }
  }

  /**
   * logga l'errore solo se non è l'api di log
   */
  private logError(event: HttpErrorResponse): void {
    if (this.isUrlToLog(event.url)) {
      this.loggerService.logError(event);
    }
  }

  /**
   * stabilisce se l'url è da loggaree
   */
  private isUrlToLog(url: string): boolean {
    return !this.isLogUrl(url) && !this.authRequestService.isUrlOutOfAuth(url);
  }

  /**
   * stabilisce se si tratta dell'api di log
   */
  private isLogUrl(url: string): boolean {
    return url && url.includes('user/action');
  }

  /**
   * crea il decoder per l'errore
   */
  private getErrorDecoder(
    event: HttpErrorResponse,
    config: ReqConfigModel
  ): ApiErrorDecoder {
    return new ApiErrorDecoder(event.error as ApiErrorDTO, {
      defaultMessage: config.errMessage
    });
  }
}
